import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { Loader2, Languages, Save } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  adminAutoTranslateEntities,
  adminListEntityTranslations,
  adminSaveEntityTranslation,
  adminTranslationStats,
} from "@/lib/cms-i18n.functions";
import { ENTITY_ARRAY_FIELDS, ENTITY_LABELS, ENTITY_TYPES, type EntityType } from "@/lib/entity-i18n";
import { LOCALES, LOCALE_NAMES } from "@/lib/i18n";

export const Route = createFileRoute("/admin/translations")({
  staticData: { sitemap: false },
  component: TranslationsPage,
});

const TARGET_LOCALES = LOCALES.filter((locale) => locale !== "en");

function asText(value: string | string[] | undefined): string {
  if (Array.isArray(value)) return value.join("\n");
  return value ?? "";
}

function TranslationsPage() {
  const [entityType, setEntityType] = useState<EntityType>("pricing_addon");
  const [locale, setLocale] = useState<string>(TARGET_LOCALES[0] ?? "uk");

  const list = useServerFn(adminListEntityTranslations);
  const save = useServerFn(adminSaveEntityTranslation);
  const auto = useServerFn(adminAutoTranslateEntities);
  const stats = useServerFn(adminTranslationStats);
  const queryClient = useQueryClient();
  const queryKey = ["admin-entity-translations", entityType, locale];

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => list({ data: { entityType, locale: locale as (typeof LOCALES)[number] } }),
  });

  const { data: statsData } = useQuery({
    queryKey: ["admin-translation-stats"],
    queryFn: () => stats({}),
  });

  const autoMutation = useMutation({
    mutationFn: () => auto({ data: { entityType, locale: locale as (typeof LOCALES)[number], batch: 8 } }),
    onSuccess: (result) => {
      toast.success(
        result.translated
          ? `Translated ${result.translated} item(s). ${result.remaining} left.`
          : "Nothing left to translate here.",
      );
      void queryClient.invalidateQueries({ queryKey });
      void queryClient.invalidateQueries({ queryKey: ["admin-translation-stats"] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const saveMutation = useMutation({
    mutationFn: (input: { entityId: string; fields: Record<string, string | string[]> }) =>
      save({ data: { entityType, locale: locale as (typeof LOCALES)[number], ...input } }),
    onSuccess: () => {
      toast.success("Translation saved");
      void queryClient.invalidateQueries({ queryKey });
      void queryClient.invalidateQueries({ queryKey: ["admin-translation-stats"] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const pending = useMemo(() => (data?.rows ?? []).filter((row) => row.missing.length).length, [data]);

  return (
    <>
      <h1 className="font-display text-3xl font-bold">Translations</h1>
      <p className="mt-3 text-sm text-muted-foreground">
        CMS copy for modules, add-ons, plans, the comparison matrix and industry cards. English is the source;
        anything left empty falls back to English on the site.
      </p>

      {statsData?.length ? (
        <div className="mt-6 flex flex-wrap gap-2">
          {statsData.map((row) => (
            <Badge
              key={row.locale}
              variant={row.done === row.total ? "default" : "outline"}
              className="cursor-pointer"
              onClick={() => setLocale(row.locale)}
            >
              {LOCALE_NAMES[row.locale as keyof typeof LOCALE_NAMES] ?? row.locale}: {row.done}/{row.total}
            </Badge>
          ))}
        </div>
      ) : null}

      <div className="mt-8 flex flex-wrap items-end gap-3">
        <div className="min-w-56">
          <label className="text-xs text-muted-foreground uppercase">Content</label>
          <Select value={entityType} onValueChange={(value) => setEntityType(value as EntityType)}>
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ENTITY_TYPES.map((type) => (
                <SelectItem key={type} value={type}>
                  {ENTITY_LABELS[type]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-44">
          <label className="text-xs text-muted-foreground uppercase">Language</label>
          <Select value={locale} onValueChange={setLocale}>
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TARGET_LOCALES.map((code) => (
                <SelectItem key={code} value={code}>
                  {LOCALE_NAMES[code] ?? code}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => autoMutation.mutate()} disabled={autoMutation.isPending || !pending}>
          {autoMutation.isPending ? (
            <Loader2 className="size-4 animate-spin" aria-hidden />
          ) : (
            <Languages className="size-4" aria-hidden />
          )}
          Translate next 8 ({pending} pending)
        </Button>
      </div>

      {isLoading ? (
        <div className="mt-10 flex justify-center">
          <Loader2 className="size-6 animate-spin text-primary" aria-hidden />
        </div>
      ) : (
        <div className="mt-8 space-y-4">
          {(data?.rows ?? []).map((row) => (
            <EntityCard
              key={row.id}
              fields={data?.fields ?? []}
              row={row}
              saving={saveMutation.isPending}
              onSave={(fields) => saveMutation.mutate({ entityId: row.id, fields })}
            />
          ))}
        </div>
      )}
    </>
  );
}

type Row = {
  id: string;
  label: string;
  source: Record<string, string | string[]>;
  target: Record<string, string | string[]>;
  status: string | null;
  missing: string[];
};

function EntityCard({
  fields,
  row,
  saving,
  onSave,
}: {
  fields: string[];
  row: Row;
  saving: boolean;
  onSave: (fields: Record<string, string | string[]>) => void;
}) {
  const [draft, setDraft] = useState<Record<string, string>>(() =>
    Object.fromEntries(fields.map((field) => [field, asText(row.target[field])])),
  );

  return (
    <div className="rounded-2xl border border-border p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-medium">{row.label}</p>
        <div className="flex items-center gap-2">
          {row.status ? <Badge variant="outline">{row.status}</Badge> : null}
          {row.missing.length ? <Badge variant="secondary">{row.missing.length} missing</Badge> : null}
          <Button size="sm" variant="outline" disabled={saving} onClick={() => onSave(draft)}>
            <Save className="size-4" aria-hidden /> Save
          </Button>
        </div>
      </div>

      <div className="mt-4 space-y-4">
        {fields.map((field) => {
          const source = asText(row.source[field]);
          if (!source.trim()) return null;
          const long = source.length > 90 || ENTITY_ARRAY_FIELDS.has(field);
          return (
            <div key={field} className="grid gap-2 md:grid-cols-2">
              <div>
                <p className="text-xs text-muted-foreground uppercase">{field} · EN</p>
                <p className="mt-1 whitespace-pre-wrap rounded-lg bg-surface p-2 text-sm">{source}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase">{field} · translation</p>
                {long ? (
                  <Textarea
                    className="mt-1 min-h-24"
                    value={draft[field] ?? ""}
                    onChange={(event) => setDraft((prev) => ({ ...prev, [field]: event.target.value }))}
                  />
                ) : (
                  <Input
                    className="mt-1"
                    value={draft[field] ?? ""}
                    onChange={(event) => setDraft((prev) => ({ ...prev, [field]: event.target.value }))}
                  />
                )}
                {ENTITY_ARRAY_FIELDS.has(field) ? (
                  <p className="mt-1 text-xs text-muted-foreground">One item per line.</p>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
